/*LIBRARY*/
import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import calculate_length from "@turf/length";
import along from "@turf/along";
import centroid from "@turf/centroid";

/*COMPONENT*/
import Modal from "../common_modal/Modal";

/*REDUX*/
import {
  set_value_layer,
  pushFeature,
  pushFeatureV2,
  editGeometry,
  editGeometryV2,
  deleteFeature,
} from "../../App/actions/layerActions";
import {
  set_value_toolbox,
  status_toolbox,
  toolbox_handle_routing_mapbox,
} from "../../App/actions/toolboxActions";
import { set_value_sini } from "../../App/actions/sini_v2";
import { get_sini_data } from "../../App/actions/get_sini_data";
import { post_activities } from "../../App/actions/activities";

/*PICTURE*/
import polygon_double_correct from "../../Assets/svg/polygon_double_correct.svg";
import polygon_double_wrong from "../../Assets/svg/polygon_double_wrong.svg";
import icon_polygon_correct from "../../Assets/svg/icon_polygon_correct.svg";
import icon_polygon_wrong from "../../Assets/svg/icon_polygon_wrong.svg";
import icon_line_correct from "../../Assets/svg/icon_line_correct.svg";
import icon_line_wrong from "../../Assets/svg/icon_line_wrong.svg";

/*FUNCTION*/
import draw_initiation from "./draw_initiation";
import on_draw_toolbox from "./on_draw_toolbox";
import on_draw_sini from "./on_draw_sini";
import { getAllFields, getGeoLayer } from "../../App/reduxHelper/layer";
import { generate_feature_base_value } from "../../App/validation/generateFeature";
import { validateKinksOfFeature } from "../../App/validation/coordinates_validation";

/*DATA*/
import domain_list from "../../Data/domain_list";
import dict from "../../Data/dict.json";

/*CONST*/
const snap_tolerance = {
  lng: 0.000009,
  lat: 0.000009,
};
const domain = window.location.host;
const allowed_to_snap = ["direct_select", "draw_polygon", "draw_line_string"];
const type_to_draw_mode = {
  Polygon: "draw_polygon",
  Point: "draw_point",
  LineString: "draw_line_string",
};

// FORMATTER: Promotes properties.key to top-level feature.id to prevent Draw cloning
const inject_stable_id = (geojson) => {
  if (!geojson || !geojson.features)
    return { type: "FeatureCollection", features: [] };
  return {
    ...geojson,
    features: geojson.features.map((f) => ({
      ...f,
      id:
        f.id ||
        f.properties?.key ||
        f.properties?.uuid ||
        Math.random().toString(),
    })),
  };
};

function convert_to_point(geojson) {
  return geojson.features
    .map((feature) => {
      if (!feature.geometry || !feature.geometry.type) return [];

      let coordinates = [];

      switch (feature.geometry.type) {
        case "Point":
          coordinates = [feature.geometry.coordinates];
          break;
        case "MultiPoint":
        case "LineString":
          coordinates = feature.geometry.coordinates;
          break;
        case "MultiLineString":
          coordinates = feature.geometry.coordinates.flat();
          break;
        case "Polygon":
          coordinates = feature.geometry.coordinates.flat();
          break;
        case "MultiPolygon":
          coordinates = feature.geometry.coordinates.flat(2);
          break;
        default:
          return [];
      }
      return coordinates.map((coord) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: coord,
        },
      }));
    })
    .flat();
}

const error_type_dict = (language = "ina", error_type, geo_type) => {
  let minOfPoints;
  if (geo_type === "Polygon") {
    minOfPoints = "3";
  } else if (geo_type === "LineString") {
    minOfPoints = "2";
  }

  if (!error_type) return {};

  const notes = {
    "not-in-same-point": {
      note: dict[
        "You have done the polygon drawing at the same point, which will end the digitizing process, minimum three points for polygon digitizing"
      ][language],
      correct:
        dict[
          "Correct: draw a polygon with sequential digitization of at least 3 different points"
        ][language],
      wrong:
        dict[
          "Wrong: drawing a polygon with digitization with only 2 points (the second and third points are at the same point)"
        ][language],
      correct_img: polygon_double_correct,
      wrong_img: polygon_double_wrong,
    },
    "self-intersection": {
      note: dict[
        "The _type_ you created intersects itself. Please create a _type_ that does not intersect itself."
      ][language].replace("_type_", geo_type),
      correct: dict["Correct: create a _type_ that does not intersect itself"][
        language
      ].replace("_type_", geo_type),
      wrong: dict["Wrong: create a _type_ that intersects itself"][
        language
      ].replace("_type_", geo_type),
      correct_img:
        geo_type === "Polygon" ? icon_polygon_correct : icon_line_correct,
      wrong_img: geo_type === "Polygon" ? icon_polygon_wrong : icon_line_wrong,
    },
    "insufficient-points": {
      note: dict[
        "The _type_ you created does not have enough points. A _type_ must have at least _minOfPoints_ points."
      ][language]
        .replace("_minOfPoints_", minOfPoints)
        .replaceAll("_type_", geo_type),
      correct: dict[
        "Correct: create a _type_ with at least _minOfPoints_ different points"
      ][language]
        .replace("_minOfPoints_", minOfPoints)
        .replaceAll("_type_", geo_type),
      wrong: dict[
        "Wrong: create a _type_ with less than _minOfPoints_ different points"
      ][language]
        .replace("_minOfPoints_", minOfPoints)
        .replaceAll("_type_", geo_type),
      correct_img: icon_polygon_correct,
      wrong_img: polygon_double_wrong,
    },
    "not-closed": {
      note: dict[
        "The polygon you created is not closed. Please ensure that the first and last points of the polygon are the same."
      ][language],
      correct:
        dict[
          "Correct: create a polygon where the first and last points are the same"
        ][language],
      wrong:
        dict[
          "Wrong: create a polygon where the first and last points are different"
        ][language],
      correct_img: icon_polygon_correct,
      wrong_img: polygon_double_wrong,
    },
    "zero-area": {
      note: dict[
        "The polygon you created has zero area. Please create a polygon with a valid area greater than zero."
      ][language],
      correct:
        dict["Correct: create a polygon with a valid area greater than zero"][
          language
        ],
      wrong: dict["Wrong: create a polygon with zero area"][language],
      correct_img: icon_polygon_correct,
      wrong_img: polygon_double_wrong,
    },
  };

  return notes[error_type];
};

const DRAW_GEO = ({
  map,
  auth,
  layer,
  toolbox,
  sini,
  set_value_layer,
  pushFeature,
  pushFeatureV2,
  editGeometry,
  editGeometryV2,
  deleteFeature,
  set_value_toolbox,
  status_toolbox,
  toolbox_handle_routing_mapbox,
  get_sini_data,
  set_value_sini,
  post_activities,
}) => {
  // eslint-disable-next-line
  const [features_draw_length, set_features_draw_length] = useState(0);
  const [modal_error, set_modal_error] = useState(false);
  const [error_type, set_error_type] = useState("");
  const [geo_type, set_geo_type] = useState("");

  const callbacksRef = useRef({});

  const prevProps = useRef({
    map_object: null,
    status_action: null,
    geometryStatus: null,
    layer_id: null,
    draw_state_platform: null,
  });

  const raf_ref = useRef(null);
  const current_feature_ref = useRef(null);
  const prev_snap_id_ref = useRef(null);
  const features_point_draw_ref = useRef([]);
  const last_snap_candidate_ref = useRef(null);

  // Safely track draw_active without triggering effect re-renders
  const draw_active_ref = useRef(layer.draw_active);

  const {
    map_object,
    draw_object,
    geometryStatus,
    layer_id,
    draw_state_platform,
    geo_layer_list,
    current_architecture,
    features_point_draw,
    draw_active,
  } = layer;

  const { status_action } = map;

  const geo_layer = geo_layer_list.find(
    (item) => item?.geo_layer?._id === layer_id,
  )?.geo_layer;
  const geometry_type = geo_layer?.type;

  useEffect(() => {
    features_point_draw_ref.current = features_point_draw || [];
  }, [features_point_draw]);

  useEffect(() => {
    draw_active_ref.current = draw_active;
  }, [draw_active]);

  const toggle_error = useCallback(() => {
    set_modal_error((prev) => !prev);
  }, []);

  const handle_esc_key = useCallback(
    (event) => {
      if (event.key === "Escape") {
        set_value_layer({
          key: "draw_active",
          value: false,
        });
        set_value_toolbox({ key: "mode_draw", value: "simple_select" });

        if (draw_object) {
          draw_object.changeMode("simple_select");
          if (map_object) {
            map_object.getCanvas().style.cursor = "";
          }
        }
      }
    },
    [draw_object, map_object, set_value_layer, set_value_toolbox],
  );

  const on_draw_update = useCallback(
    (snap_point) => {
      if (!geometryStatus) return;

      const mode = draw_object?.getMode();

      if (!draw_object || !snap_point || !allowed_to_snap.includes(mode))
        return;

      const feature_current = current_feature_ref.current;
      if (!feature_current) return;

      const { id, geometry, properties } = feature_current;
      const { type, coordinates } = geometry;
      const [lng_snap, lat_snap] = snap_point.geometry.coordinates;

      let new_coordinates = coordinates;

      if (type === "Polygon") {
        const ring = [...coordinates[0]];
        for (let i = 0; i < ring.length; i++) {
          const [lng, lat] = ring[i];
          if (
            Math.abs(lng - lng_snap) < snap_tolerance.lng &&
            Math.abs(lat - lat_snap) < snap_tolerance.lat
          ) {
            ring[i] = [lng_snap, lat_snap];
            break;
          }
        }
        new_coordinates = [ring];
      } else if (type === "LineString") {
        const line = [...coordinates];
        for (let i = 0; i < line.length; i++) {
          const [lng, lat] = line[i];
          if (
            Math.abs(lng - lng_snap) < snap_tolerance.lng &&
            Math.abs(lat - lat_snap) < snap_tolerance.lat
          ) {
            line[i] = [lng_snap, lat_snap];
            break;
          }
        }
        new_coordinates = line;
      } else {
        return;
      }

      const feature_updated = {
        type: "Feature",
        id,
        properties: { ...properties },
        geometry: {
          type,
          coordinates: new_coordinates,
        },
      };

      current_feature_ref.current = feature_updated;

      const features_draw = draw_object.getAll().features;
      const updated_features = features_draw.map((f) =>
        f.id === id ? feature_updated : f,
      );

      draw_object.set({
        type: "FeatureCollection",
        features: updated_features,
      });

      if (mode === "direct_select") {
        const all_ids = draw_object.getAll().features.map((f) => f.id);
        const feature_exists = all_ids.includes(id);

        if (feature_exists) {
          try {
            draw_object.changeMode("direct_select", { featureId: String(id) });
          } catch (err) {}
        }
      }

      set_value_layer({
        key: "features_point_snap",
        value: [],
      });
    },
    [geometryStatus, draw_object, set_value_layer],
  );

  const handle_cursor_move = useCallback(
    (e) => {
      if (!geometryStatus || !map_object) return;

      const mode = draw_object?.getMode();
      if (!allowed_to_snap.includes(mode)) return;

      if (raf_ref.current) cancelAnimationFrame(raf_ref.current);

      raf_ref.current = requestAnimationFrame(() => {
        const coords = map_object.unproject([e.clientX, e.clientY]);
        const long_center = coords.lng;
        const lat_center = coords.lat;

        const long_start = long_center - snap_tolerance.lng;
        const lat_start = lat_center - snap_tolerance.lat;
        const long_end = long_center + snap_tolerance.lng;
        const lat_end = lat_center + snap_tolerance.lat;

        const safe_features_point_draw = features_point_draw_ref.current;

        let features_point_snap_local = safe_features_point_draw.filter(
          (feature) => {
            const long_test = feature?.geometry?.coordinates?.[0];
            const lat_test = feature?.geometry?.coordinates?.[1];
            return (
              long_test > long_start &&
              long_test < long_end &&
              lat_test > lat_start &&
              lat_test < lat_end
            );
          },
        );

        if (features_point_snap_local.length > 0) {
          const current_feature = current_feature_ref.current;

          if (current_feature) {
            const geojson = {
              type: "FeatureCollection",
              features: [current_feature],
            };

            const current_point_features = convert_to_point(geojson);

            const current_coords_set = new Set(
              current_point_features.map(
                (f) =>
                  `${f.geometry.coordinates[0]},${f.geometry.coordinates[1]}`,
              ),
            );

            features_point_snap_local = features_point_snap_local.filter(
              (feature) => {
                const [lng, lat] = feature?.geometry?.coordinates || [];
                return !current_coords_set.has(`${lng},${lat}`);
              },
            );

            if (features_point_snap_local.length > 0) {
              on_draw_update(features_point_snap_local[0]);
            }
          }
        }

        features_point_snap_local =
          features_point_snap_local.length > 0
            ? [features_point_snap_local[0]]
            : [];

        const layer_snap_id = "layer_snap_id";
        const geojson_snap = {
          type: "FeatureCollection",
          features: features_point_snap_local,
        };

        if (!map_object.getSource(layer_snap_id)) {
          map_object.addSource(layer_snap_id, {
            type: "geojson",
            data: geojson_snap,
          });
        } else {
          map_object.getSource(layer_snap_id).setData(geojson_snap);
        }

        if (!map_object.getLayer(layer_snap_id)) {
          map_object.addLayer({
            id: layer_snap_id,
            type: "circle",
            source: layer_snap_id,
            paint: {
              "circle-color": "red",
              "circle-radius": 5,
              "circle-stroke-width": 2,
              "circle-stroke-color": "#fff",
            },
            layout: {
              visibility: "visible",
            },
          });
        }

        last_snap_candidate_ref.current = features_point_snap_local[0] ?? null;

        const next_snap_id = features_point_snap_local?.[0]?.id ?? null;
        if (next_snap_id !== prev_snap_id_ref.current) {
          prev_snap_id_ref.current = next_snap_id;
          set_value_layer({
            key: "features_point_snap",
            value: features_point_snap_local,
          });
        }
      });
    },
    [geometryStatus, map_object, draw_object, on_draw_update, set_value_layer],
  );

  const feature_error_validation = useCallback(
    (event) => {
      if (!draw_object) return;

      const feature = event?.features?.[0];
      const kinks = validateKinksOfFeature(feature);

      if (!kinks?.is_valid) {
        set_modal_error(true);
        set_error_type(kinks?.errors?.[0]?.type);
        set_geo_type(feature.geometry.type);
        draw_object.delete(feature.id);
      }

      return kinks?.is_valid;
    },
    [draw_object],
  );

  const on_draw_click = useCallback(async (e) => {}, []);

  const on_create_geometry = useCallback(() => {
    const draw_mode = type_to_draw_mode?.[geometry_type] || "";
    if (map_object && draw_mode) {
      map_object.getCanvas().style.cursor = "crosshair";
      set_value_layer({
        key: "draw_active",
        value: true,
      });

      setTimeout(() => {
        if (draw_object) {
          try {
            const current_mode = draw_object.getMode();
            if (current_mode !== draw_mode) {
              draw_object.changeMode(draw_mode);
            }
          } catch (error) {
            console.error("Mapbox Draw mode reset error:", error);
          }
        }
      }, 200);
    }
  }, [draw_object, map_object, set_value_layer, geometry_type]);

  const on_create = useCallback(
    async (event) => {
      try {
        if (last_snap_candidate_ref.current) {
          const [lng_snap, lat_snap] =
            last_snap_candidate_ref.current.geometry.coordinates;
          last_snap_candidate_ref.current = null;

          const feature = event?.features?.[0];
          if (feature) {
            const { type, coordinates } = feature.geometry;
            if (type === "Polygon") {
              const ring = [...coordinates[0]];
              const idx = ring.length - 2;
              if (idx >= 1) {
                ring[idx] = [lng_snap, lat_snap];
                ring[ring.length - 1] = ring[0];
                feature.geometry = { type, coordinates: [ring] };
                event.features[0] = feature;
              }
            } else if (type === "LineString") {
              const line = [...coordinates];
              line[line.length - 1] = [lng_snap, lat_snap];
              feature.geometry = { type, coordinates: line };
              event.features[0] = feature;
            }
          }
        }

        const is_valid_feature = feature_error_validation(event);
        if (!is_valid_feature) return;

        if (draw_state_platform === "geometry") {
          const geometry = event?.features?.[0]?.geometry;
          const id = event?.features?.[0]?.id;

          if (layer_id) {
            const geo_layer = getGeoLayer(layer);
            const fields = getAllFields(geo_layer).filter(
              (field) => !field?.parent_uuid,
            );
            const prop = { key: id, ...generate_feature_base_value(fields) };
            let feature = {
              type: "Feature",
              properties: prop,
              geometry: geometry,
              key: id,
              isHide: false,
              formStatus: { status: "digitasi" },
            };
            const body = {
              geo_layer_id: layer_id,
              feature,
            };
            if (current_architecture === "v2") {
              pushFeatureV2(body);
            } else {
              pushFeature(body);
            }

            if (
              [
                "localhost:3003",
                "dsdajakarta.mapid.io",
                "dsdajakarta-alpha.mapid.io",
                "dsdajakarta-beta.mapid.io",
              ].includes(domain)
            ) {
              const { type_2 } = geo_layer;
              const type = geometry?.type;
              let longitude, latitude;

              if (type === "Point") {
                [longitude, latitude] = geometry?.coordinates;
              } else if (type === "LineString") {
                const length_km = calculate_length(feature, {
                  units: "kilometers",
                });
                const center = along(feature, length_km / 2, {
                  units: "kilometers",
                });
                [longitude, latitude] = center?.geometry.coordinates;
              } else {
                const center = centroid({
                  type: "FeatureCollection",
                  features: [feature],
                });
                [longitude, latitude] = center?.geometry.coordinates;
              }

              const handleFullScreen = () => {
                set_value_layer({
                  key: "modal_edit_feature_properties",
                  value: true,
                });
                set_value_layer({
                  key: "latitude_selected",
                  value: latitude,
                });
                set_value_layer({
                  key: "longitude_selected",
                  value: longitude,
                });
                set_value_layer({
                  key: "fields_selected",
                  value: fields,
                });
                set_value_layer({
                  key: "properties_selected",
                  value: feature?.properties,
                });
                set_value_layer({
                  key: "type_2_selected",
                  value: type_2,
                });
                set_value_layer({
                  key: "feature_key_selected",
                  value: feature?.properties?.key,
                });
                set_value_layer({
                  key: "geo_layer_selected",
                  value: geo_layer,
                });
              };

              if (geo_layer?.fields?.length) {
                handleFullScreen();
              }
            }
          }

          on_create_geometry();
        } else if (draw_state_platform === "toolbox") {
          on_draw_toolbox({
            event,
            toolbox: toolbox,
            set_value_toolbox: set_value_toolbox,
            status_toolbox: status_toolbox,
            toolbox_handle_routing_mapbox: toolbox_handle_routing_mapbox,
            map_object,
            draw_object,
          });
        } else if (draw_state_platform === "sini") {
          const siniDomain = auth?.domain;
          const show_survey_1 = domain_list?.[siniDomain]?.show_survey_1;

          on_draw_sini({
            event,
            sini: sini,
            get_sini_data: get_sini_data,
            set_value_sini: set_value_sini,
            set_value_layer: set_value_layer,
            show_survey_1,
            post_activities: post_activities,
          });

          // --- ADD THESE LINES TO RESET CURSOR AND DRAW MODE ---
          if (draw_object) {
            draw_object.deleteAll();
            draw_object.changeMode("simple_select"); // Return to default mapbox draw mode
          }

          if (map_object) {
            map_object.getCanvas().style.cursor = ""; // Return cursor to default
          }

          set_value_layer({
            key: "draw_active",
            value: false, // Tell redux drawing is done
          });

          set_value_toolbox({
            key: "mode_draw",
            value: "simple_select",
          });
          // -----------------------------------------------------
        }
      } catch (error) {}
    },
    [
      feature_error_validation,
      draw_state_platform,
      layer_id,
      on_create_geometry,
      layer,
      current_architecture,
      pushFeatureV2,
      pushFeature,
      set_value_layer,
      toolbox,
      set_value_toolbox,
      status_toolbox,
      toolbox_handle_routing_mapbox,
      map_object,
      draw_object,
      auth?.domain,
      sini,
      get_sini_data,
      set_value_sini,
      post_activities,
    ],
  );

  const on_update = useCallback(
    (event) => {
      if (["change_coordinates", "move"].includes(event?.action)) {
        const feature_key =
          event?.features?.[0]?.properties?.key || event?.features?.[0]?.id;
        const geometry = event?.features?.[0]?.geometry;
        if (feature_key && geometry && layer_id) {
          const body = {
            feature_key: feature_key,
            geo_layer_id: layer_id,
            geometry: geometry,
          };
          if (current_architecture === "v2") {
            editGeometryV2(body);
          } else {
            editGeometry(body);
          }
        }
      }
    },
    [layer_id, current_architecture, editGeometryV2, editGeometry],
  );

  const on_delete = useCallback(
    (event) => {
      if (map_object) {
        map_object.getCanvas().style.cursor = "";
      }
      const feature_key =
        event?.features?.[0]?.properties?.key || event?.features?.[0]?.id;
      const body = {
        feature_key: feature_key,
        geo_layer_id: layer_id,
      };
      if (layer_id && feature_key) {
        deleteFeature(body);
      }
    },
    [map_object, layer_id, deleteFeature],
  );

  const on_modechange = useCallback((e) => {}, []);

  useEffect(() => {
    callbacksRef.current = {
      on_create,
      on_update,
      on_delete,
      on_draw_click,
      on_modechange,
      handle_cursor_move,
      on_draw_update_snap: on_draw_update,
      handle_canvas_click: (e) => {
        const mode = draw_object?.getMode();
        if (!allowed_to_snap.includes(mode)) return;

        const coords = map_object?.unproject([e.clientX, e.clientY]);
        if (!coords) return;

        const long_center = coords.lng;
        const candidates = features_point_draw_ref.current.filter((f) => {
          const lx = f?.geometry?.coordinates?.[0];
          const ly = f?.geometry?.coordinates?.[1];
          return (
            lx > long_center - snap_tolerance.lng &&
            lx < long_center + snap_tolerance.lng &&
            ly > long_center - snap_tolerance.lat &&
            ly < long_center + snap_tolerance.lat
          );
        });
        if (candidates.length === 0) return;

        const [lng_snap, lat_snap] = candidates[0].geometry.coordinates;

        requestAnimationFrame(() => {
          const all_features = draw_object.getAll().features;
          if (!all_features.length) return;
          const feat = all_features[all_features.length - 1];
          const { id, geometry, properties } = feat;
          const { type, coordinates } = geometry;

          let new_coordinates;
          if (type === "Polygon") {
            const ring = [...coordinates[0]];
            const idx = ring.length - 3;
            if (idx < 1) return;
            ring[idx] = [lng_snap, lat_snap];
            new_coordinates = [ring];
          } else if (type === "LineString") {
            const line = [...coordinates];
            const idx = line.length - 2;
            if (idx < 1) return;
            line[idx] = [lng_snap, lat_snap];
            new_coordinates = line;
          } else {
            return;
          }

          const feature_updated = {
            type: "Feature",
            id,
            properties: { ...properties },
            geometry: { type, coordinates: new_coordinates },
          };
          current_feature_ref.current = feature_updated;
          const updated = all_features.map((f) =>
            f.id === id ? feature_updated : f,
          );
          draw_object.set({ type: "FeatureCollection", features: updated });
        });
      },
    };
  });

  useEffect(() => {
    document.addEventListener("keydown", handle_esc_key);
    return () => {
      document.removeEventListener("keydown", handle_esc_key);
    };
  }, [handle_esc_key]);

  useEffect(() => {
    if (!map_object || !draw_object) return;

    const sync_current_feature = () => {
      const all_features = draw_object.getAll().features;
      if (all_features.length > 0) {
        current_feature_ref.current = all_features[all_features.length - 1];
      } else {
        current_feature_ref.current = null;
      }
    };

    map_object.on("draw.render", sync_current_feature);
    map_object.on("draw.create", sync_current_feature);
    map_object.on("draw.update", sync_current_feature);
    map_object.on("draw.selectionchange", sync_current_feature);
    map_object.on("draw.modechange", sync_current_feature);

    return () => {
      map_object.off("draw.render", sync_current_feature);
      map_object.off("draw.create", sync_current_feature);
      map_object.off("draw.update", sync_current_feature);
      map_object.off("draw.selectionchange", sync_current_feature);
      map_object.off("draw.modechange", sync_current_feature);
    };
  }, [map_object, draw_object]);

  useEffect(() => {
    if (!map_object || !draw_object) return;

    const canvas = map_object.getCanvas();
    const proxy_click = (e) => callbacksRef.current.handle_canvas_click?.(e);

    canvas.addEventListener("click", proxy_click);

    return () => {
      canvas.removeEventListener("click", proxy_click);
    };
  }, [map_object, draw_object]);

  useEffect(() => {
    return () => {
      if (raf_ref.current) cancelAnimationFrame(raf_ref.current);
    };
  }, []);

  useEffect(() => {
    const proxyHandler = (e) => callbacksRef.current.handle_cursor_move?.(e);
    return () => {
      if (map_object) {
        map_object.getCanvas().removeEventListener("mousemove", proxyHandler);
      }
    };
  }, [map_object]);

  useEffect(() => {
    const prev = prevProps.current;
    const proxyHandler = (e) => callbacksRef.current.handle_cursor_move?.(e);

    if (map_object !== prev.map_object) {
      if (map_object && !draw_object) {
        const draw_body = {
          map_object,
          on_create: (e) => callbacksRef.current.on_create?.(e),
          on_update: (e) => callbacksRef.current.on_update?.(e),
          on_delete: (e) => callbacksRef.current.on_delete?.(e),
          on_draw_click: (e) => callbacksRef.current.on_draw_click?.(e),
          on_modechange: (e) => callbacksRef.current.on_modechange?.(e),
          set_value_layer,
        };
        if (map_object.loaded()) draw_initiation(draw_body);
        else
          map_object.once("load", () => {
            draw_initiation(draw_body);
          });
      }
    }

    const status_action_changed = status_action !== prev.status_action;
    const geometryStatus_changed = geometryStatus !== prev.geometryStatus;
    const layer_id_changed = layer_id !== prev.layer_id;
    const draw_state_platform_changed =
      draw_state_platform !== prev.draw_state_platform;

    if (
      (status_action_changed ||
        geometryStatus_changed ||
        layer_id_changed ||
        draw_state_platform_changed) &&
      draw_state_platform !== "toolbox"
    ) {
      if (geometryStatus && layer_id) {
        const geo_layer = geo_layer_list.find(
          (item) => item?.geo_layer?._id === layer_id,
        )?.geo_layer;

        // Pass the raw Redux data through the stable ID formatter
        const raw_geojson = geo_layer?.geojson || {
          type: "FeatureCollection",
          features: [],
        };
        const safe_geojson = inject_stable_id(raw_geojson);

        // PROTECTION: Safely upsert data without wiping if user is drawing
        if (
          status_action_changed &&
          !geometryStatus_changed &&
          !layer_id_changed &&
          draw_active_ref.current
        ) {
          if (draw_object) {
            draw_object.add(safe_geojson);
          }
          prevProps.current = {
            map_object,
            status_action,
            geometryStatus,
            layer_id,
            draw_state_platform,
          };
          return;
        }

        // If NOT actively drawing, standard full sync
        if (draw_object) {
          draw_object.deleteAll();
          draw_object.add(safe_geojson);

          if (draw_active_ref.current) {
            const draw_mode = type_to_draw_mode?.[geo_layer?.type];
            if (draw_mode) {
              try {
                draw_object.changeMode(draw_mode);
              } catch (e) {}
            }
          } else {
            draw_object.changeMode("simple_select");
          }
        }

        const features_point_draw_local = convert_to_point(safe_geojson);
        set_value_layer({
          key: "features_point_draw",
          value: features_point_draw_local,
        });

        prev_snap_id_ref.current = null;
        current_feature_ref.current = null;

        if (map_object) {
          map_object.getCanvas().addEventListener("mousemove", proxyHandler);
        }
      } else {
        if (map_object) {
          map_object.getCanvas().removeEventListener("mousemove", proxyHandler);
        }
        if (draw_object) draw_object.deleteAll();
      }
    }

    prevProps.current = {
      map_object,
      status_action,
      geometryStatus,
      layer_id,
      draw_state_platform,
    };
  }, [
    map_object,
    draw_object,
    status_action,
    geometryStatus,
    layer_id,
    draw_state_platform,
    geo_layer_list,
    set_value_layer,
  ]);

  const language = localStorage?.language || auth.language;
  const geo_error_notes = error_type_dict(language, error_type, geo_type);

  return (
    <main>
      {modal_error && (
        <Modal
          modalSize="small"
          id="modal"
          isOpen={modal_error}
          onClose={toggle_error}
        >
          <div className="box-body">
            <p className="text_big margin_bottom">
              {dict?.["Drawing error"]?.[language]}
            </p>
            <p className="text_medium sub_color margin_bottom_extra">
              {geo_error_notes?.note}
            </p>
            <main className="text_center">
              <section className="container two_container_equal background_grey_light margin_bottom_extra">
                <div className="text_center">
                  <img
                    className="margin_bottom"
                    alt="polygon_double_correct"
                    src={geo_error_notes?.correct_img}
                    width={89}
                    style={{ maxWidth: "30vw" }}
                  />
                  <br />
                  <p className="text_medium sub_color">
                    {geo_error_notes?.correct}
                  </p>
                </div>
                <div className="text_center">
                  <img
                    className="margin_bottom"
                    alt="polygon_double_wrong"
                    src={geo_error_notes?.wrong_img}
                    width={89}
                    style={{ maxWidth: "30vw" }}
                  />
                  <br />
                  <p className="text_medium sub_color">
                    {geo_error_notes?.wrong}
                  </p>
                </div>
              </section>
              <button
                className="button_big background_black"
                onClick={toggle_error}
              >
                {dict?.["Close"]?.[language]}
              </button>
            </main>
          </div>
        </Modal>
      )}
    </main>
  );
};

const mapStateToProps = (state) => ({
  map: state.map,
  auth: state.auth,
  layer: state.layer,
  toolbox: state.toolbox,
  sini: state.sini,
});

export default connect(mapStateToProps, {
  set_value_layer,
  pushFeature,
  pushFeatureV2,
  editGeometry,
  editGeometryV2,
  deleteFeature,
  set_value_toolbox,
  get_sini_data,
  set_value_sini,
  status_toolbox,
  toolbox_handle_routing_mapbox,
  post_activities,
})(DRAW_GEO);
